import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function NotFound() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FC]">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-[#2C7DA0]/5 blur-3xl" />
          <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-[#0A2540]/5 blur-3xl" />
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-[#7EC8E3]/10 blur-2xl animate-pulse" />
        </div>

        <div className="relative z-10 text-center px-4 py-20 max-w-4xl mx-auto">
          {/* 404 Illustration */}
          <div className="mb-8">
            <div className="relative inline-block">
              {/* Large 404 Background */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-[12rem] md:text-[16rem] font-black text-[#0A2540]/5 select-none leading-none">
                  404
                </span>
              </div>
              
              {/* Main Content */}
              <div className="relative flex flex-col items-center">
                {/* Icon */}
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#2C7DA0] to-[#0A2540] flex items-center justify-center mb-6 shadow-xl">
                  <i className="ri-compass-3-line text-4xl text-white" />
                </div>
                
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2C7DA0]/10 text-[#2C7DA0] text-sm font-semibold mb-6">
                  <i className="ri-error-warning-line" />
                  <span>Page Not Found</span>
                </div>
              </div>
            </div>
          </div>

          {/* Title & Description */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">
              {t('notFound.title', '您访问的页面不存在')}
            </h1>
            <p className="text-lg text-[#0A2540]/60 mb-2">
              {t('notFound.description', '抱歉，您访问的页面可能已被移除或地址有误')}
            </p>
            <p className="text-sm text-[#0A2540]/40 font-mono">
              {/* 显示当前路径 */}
              <span className="inline-flex items-center gap-1">
                <i className="ri-link" />
                <span className="bg-[#0A2540]/5 px-3 py-1 rounded-md">
                  {window.location.pathname}
                </span>
              </span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              to="/"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-[#0A2540] text-white rounded-xl font-medium hover:bg-[#0A2540]/90 transition-all shadow-lg shadow-[#0A2540]/20"
            >
              <i className="ri-home-4-line text-lg group-hover:-translate-x-0.5 transition-transform" />
              {t('notFound.goHome', '返回首页')}
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0A2540] border border-[#0A2540]/10 rounded-xl font-medium hover:bg-[#0A2540]/5 transition-all"
            >
              <i className="ri-arrow-left-line text-lg group-hover:-translate-x-0.5 transition-transform" />
              {t('notFound.goBack', '返回上页')}
            </button>
            
            <Link
              to="/events"
              className="group inline-flex items-center gap-2 px-6 py-3 bg-[#2C7DA0]/10 text-[#2C7DA0] rounded-xl font-medium hover:bg-[#2C7DA0]/20 transition-all"
            >
              <i className="ri-calendar-event-line text-lg" />
              {t('notFound.viewEvents', '查看活动')}
            </Link>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#0A2540]/5">
            <h3 className="text-sm font-semibold text-[#0A2540]/60 uppercase tracking-wider mb-4">
              {t('notFound.quickLinks', '快速链接')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { to: '/', icon: 'ri-home-4-line', label: t('nav.home', '首页') },
                { to: '/about', icon: 'ri-global-line', label: t('nav.about', '关于联盟') },
                { to: '/events', icon: 'ri-calendar-event-line', label: t('nav.events', '活动') },
                { to: '/guests', icon: 'ri-user-star-line', label: t('nav.guests', '嘉宾') },
              ].map(({ to, icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#F7F9FC] hover:bg-[#2C7DA0]/5 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#2C7DA0]/10 flex items-center justify-center group-hover:bg-[#2C7DA0]/20 transition-colors">
                    <i className={`${icon} text-[#2C7DA0] text-lg`} />
                  </div>
                  <span className="text-sm font-medium text-[#0A2540]/80">{label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-[#0A2540]/40">
            <i className="ri-information-line" />
            <span>
              {t('notFound.help', '如果您认为这是一个错误，请')}
              <a 
                href="mailto:admin@gtraweb3.com" 
                className="text-[#2C7DA0] hover:underline ml-1"
              >
                {t('notFound.contactUs', '联系我们')}
              </a>
            </span>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
